import React from "react";
import {useMessages} from "i18n";
import type {SponsorOptions, TakeoverTemplateKey} from "../sponsorBooking";
import {
	SPONSOR_LOGO_TYPES,
	TEMPLATE_OPTIONS,
	WIPE_DIRECTIONS,
	allowsOption,
	offersOption,
	type SponsorOptionKey,
	type SponsorPickedAsset,
} from "../sponsorOptions";
import {AssetPickerField} from "./AssetPickerField";
import {FocalPointField, type FocalPointCopy} from "./FocalPointField";
import {OptionChoiceField} from "./OptionChoiceField";
import {PosterColorField} from "./PosterColorField";
import {SublineField} from "./SublineField";
import {TextPlacementField} from "./TextPlacementField";
import {OptionsGrid, OptionsHead, OptionsLede, OptionsTitle} from "./SponsorOptionsPanel.styles";

/**
 * Every setting a WALLPAPER treatment takes, in one panel (kitecorp/transferify-ui#1868).
 *
 * WHICH controls appear is read from {@link TEMPLATE_OPTIONS} — the same table
 * `validateSponsorOptions` judges against and `sponsorOptions.test.ts` asserts. That is the whole
 * arrangement: a control cannot offer a setting the server would refuse, and a setting the server
 * accepts cannot silently stop being offered, because neither side has its own list. The wipe's
 * direction is the visible consequence — it appears for WIPE_REVEAL and for nothing else, from one row
 * of the table rather than from a condition written here.
 *
 * Dumb by mandate (epic #26): it holds no state, decides no rule, and takes every value and callback as
 * a prop, so the sender's own page-theme picker (kitecorp/transferify-ui#1869) mounts it over its own
 * state without touching the sponsor form.
 *
 * ## Two things a caller supplies, and one it cannot
 *
 * The LABELS and HINTS arrive as {@link SponsorOptionsPanelProps.copy}, because the two surfaces are
 * genuinely addressing different people: an advertiser has a tagline and a button, and "paints your
 * tagline and your button" is simply false said to a sender whose page theme has neither. One catalog
 * for both would have forced one of them to read a sentence about a control they do not have.
 *
 * The enum WORDS do not: `direction.values` and the placement's two axes are always read from
 * `sponsor.options`, which the catalog documents as the one place each of those constants becomes
 * English. They name the api's own values rather than either surface's product, so a second translation
 * of them could only ever be a way for the two to disagree about what "BOTTOM_UP" means.
 *
 * The LOGO is a whole control rather than a set of props, and it is nullable: a sender's page theme
 * offers no mark at all (the api's page-theme contract carries no logo asset), so passing `null`
 * withholds the control instead of rendering an upload nothing would store.
 */

/**
 * The wording ONE surface gives these controls. Every string a reader sees except the enum words, which
 * are shared — see this module's header for why the line is drawn there.
 */
export interface SponsorOptionsCopy {
	title: string;
	lede: string;
	direction: {label: string; hint: string};
	placement: {label: string; hint: string};
	/** The colour of the words, which travels as `brandColor` (kitecorp/transferify-ui#2416). */
	textColor: {label: string; hint: string; noColor: string};
	/** The solid ground behind the words, `textBackgroundColor` on the wire. */
	textBackground: {label: string; hint: string; noColor: string};
	subline: {label: string; placeholder: string; hint: string};
}

/**
 * The crop control, or `null` on a surface that does not offer one
 * (kitecorp/transferify-ui#2143).
 *
 * Grouped and nullable for exactly the reason {@link SponsorLogoControl} is, and the nullability does
 * real work rather than mirroring it: the marker needs the PICKED MEDIA to be dragged over, and the
 * three surfaces that mount this panel hold their creative in three different places. It is also an
 * advertiser's decision - a wallpaper BOOKING is one file cropped by every recipient's window - so the
 * sender's page theme and the per-transfer builder pass `null` and show no control at all, which is
 * why `focalPoint` being in `TEMPLATE_OPTIONS` is not on its own what puts a control on screen.
 */
export interface SponsorFocalPointControl {
	copy: FocalPointCopy;
	/** The still the marker is dragged over: the first picture, or a video treatment's poster frame. */
	mediaUrl: string | null;
}

/**
 * The brand-mark control, or `null` on a surface that has nowhere to store one.
 *
 * Grouped rather than spread across four props because the four are one control: a caller that has a
 * logo has all of them, and a caller that does not have none. Flattened, "no logo" would have been four
 * separate nulls and a `false`, any one of which could be forgotten.
 */
export interface SponsorLogoControl {
	copy: {
		label: string;
		hint: string;
		add: string;
		addAria: string;
		replaceAria: string;
		remove: string;
		alt: string;
	};
	/**
	 * The mark as uploaded.
	 *
	 * Part of the POSTER rather than of the creative: it is laid in the corner opposite the download
	 * card, in the composition these controls are arranging. The treatment's pictures are a different
	 * decision and live in their own step.
	 */
	asset: SponsorPickedAsset | null;
	/** True while any upload is in flight; the picker shuts so two cannot race. */
	uploading: boolean;
	onPick: (file: File, opener: HTMLElement) => void;
	onRemove: () => void;
}

export interface SponsorOptionsPanelProps {
	template: TakeoverTemplateKey;
	options: SponsorOptions;
	/** Sets one option, or clears it with `undefined`. */
	setOption: <K extends SponsorOptionKey>(key: K, value: SponsorOptions[K] | undefined) => void;
	copy: SponsorOptionsCopy;
	/** The mark control, or `null` to offer none at all. */
	logo: SponsorLogoControl | null;
	/** The crop control, or `null` to offer none at all. See {@link SponsorFocalPointControl}. */
	focalPoint?: SponsorFocalPointControl | null;
	/**
	 * The radio groups' form-name prefix.
	 *
	 * A prop because two panels must never share it: same-name radios are ONE exclusive group to the
	 * browser however they are nested, so a second panel on a page would silently steer the first.
	 */
	namePrefix: string;
	/** Already-translated message for one control, or `null`. */
	errorText: (field: SponsorOptionKey | "logo") => string | null;
	/**
	 * Marks the subline visited, so its length message waits until the reader has left the field.
	 *
	 * Optional: the bound is also the input's `maxLength`, so the only drafts that can break it are
	 * restored or hand-built ones. A surface with no such state — the sender's page theme, whose draft
	 * lives for the life of the sheet — shows the message as soon as it applies and passes none.
	 */
	onSublineBlur?: () => void;
	/**
	 * Draws the subline FIRST, right under the lede, ahead of the placement, direction and colour
	 * controls (owner 2026-09-03, kitecorp/transferify-ui#1953). The transfer builder asks for it: there
	 * the words are what the sender came to write and the rest are settings about those words.
	 *
	 * A prop rather than the panel's new default, because this panel is shared. The sponsor form and the
	 * account page-theme picker keep the order they were reviewed with, and a change to what an advertiser
	 * sees is a decision for that surface, not a side effect of the builder's. Order only: the control,
	 * its copy and its handlers are the same in either position.
	 */
	sublineFirst?: boolean;
}

/** The `accept` attribute for a picker, derived from the list its validator judges against. */
const acceptOf = (types: readonly string[]): string => types.join(",");

export function SponsorOptionsPanel({
	template, options, setOption, copy, logo, focalPoint = null, namePrefix, errorText, onSublineBlur,
	sublineFirst = false,
}: SponsorOptionsPanelProps): React.ReactElement {
	// The enum WORDS only — never a label or a hint. See this module's header.
	const m = useMessages().sponsor.options;

	// ONE element, placed by `sublineFirst` (see the prop): built once so the two positions cannot drift
	// in what they render.
	const subline = allowsOption(template, "subline") && (
		<SublineField
			label={copy.subline.label}
			placeholder={copy.subline.placeholder}
			hint={copy.subline.hint}
			value={options.subline ?? ""}
			onChange={value => setOption("subline", value)}
			onBlur={onSublineBlur ?? (() => undefined)}
			error={errorText("subline")}
		/>
	);

	return (
		<>
			<OptionsHead>
				<OptionsTitle>{copy.title}</OptionsTitle>
				<OptionsLede>{copy.lede}</OptionsLede>
			</OptionsHead>
			<OptionsGrid>
				{sublineFirst && subline}

				{allowsOption(template, "direction") && (
					<OptionChoiceField
						label={copy.direction.label}
						hint={copy.direction.hint}
						name={`${namePrefix}-direction`}
						values={WIPE_DIRECTIONS}
						value={options.direction}
						nameOf={direction => m.direction.values[direction]}
						onChange={direction => setOption("direction", direction)}
						error={errorText("direction")}
					/>
				)}

				{allowsOption(template, "textPlacement") && (
					<TextPlacementField
						label={copy.placement.label}
						hint={copy.placement.hint}
						name={`${namePrefix}-placement`}
						value={options.textPlacement}
						cellName={(vertical, horizontal) =>
							m.placement.cell(m.placement.vertical[vertical], m.placement.horizontal[horizontal])}
						onChange={placement => setOption("textPlacement", placement)}
						error={errorText("textPlacement")}
					/>
				)}

				{/*
				  * The two colour rows that replaced the Light/Dark tone and the separate brand colour
				  * (owner 2026-09-12/13, kitecorp/transferify-ui#2416): the words' colour, then the ground
				  * behind them, each a small swatch set with a no-colour swatch.
				  */}
				{allowsOption(template, "brandColor") && (
					<PosterColorField
						label={copy.textColor.label}
						hint={copy.textColor.hint}
						noColorLabel={copy.textColor.noColor}
						value={options.brandColor}
						onChange={color => setOption("brandColor", color)}
						error={errorText("brandColor")}
					/>
				)}

				{allowsOption(template, "textBackgroundColor") && (
					<PosterColorField
						label={copy.textBackground.label}
						hint={copy.textBackground.hint}
						noColorLabel={copy.textBackground.noColor}
						value={options.textBackgroundColor}
						onChange={color => setOption("textBackgroundColor", color)}
						error={errorText("textBackgroundColor")}
					/>
				)}

				{/*
				  * The crop, AFTER the poster settings and before the mark: it is about the picture rather
				  * than about the words, and it is the one control that needs the creative to already be
				  * there. A surface that offers none passes `null` and this is absent rather than
				  * disabled, exactly as the mark below is.
				  */}
				{focalPoint !== null && offersOption(template, "focalPoint") && (
					<FocalPointField
						copy={focalPoint.copy}
						mediaUrl={focalPoint.mediaUrl}
						value={options.focalPoint}
						onChange={point => setOption("focalPoint", point)}
						error={errorText("focalPoint")}
					/>
				)}

				{!sublineFirst && subline}

				{/*
				  * The mark is offered on every wallpaper a surface can store one for, which is why it is
				  * not in the option table: `TEMPLATE_OPTIONS` describes the `options` COLUMN, and a logo is
				  * an asset with a role. A page theme has no such role, so it passes `null` and the control
				  * is absent rather than disabled — an upload nothing would keep is worse than no upload.
				  */}
				{logo !== null && (
					<AssetPickerField
						label={logo.copy.label}
						hint={logo.copy.hint}
						accept={acceptOf(SPONSOR_LOGO_TYPES)}
						addAria={logo.copy.addAria}
						replaceAria={logo.copy.replaceAria}
						addLabel={logo.copy.add}
						removeLabel={logo.copy.remove}
						url={logo.asset?.url ?? null}
						alt={logo.copy.alt}
						filename={logo.asset?.filename ?? null}
						disabled={logo.uploading}
						busy={logo.uploading}
						onPick={logo.onPick}
						onRemove={logo.onRemove}
						error={errorText("logo")}
					/>
				)}

			</OptionsGrid>
		</>
	);
}
